import { produce } from 'immer';
import { useCallback, useContext } from 'react';
import { DEFAULT_INJECT_OPERATOR, InjectVariableOptions, PanelDefinition, QueryDefinition } from '@/core';
import { getInjectVariableFunction, initializeDataQueryPlugin, usePluginRegistry } from '@/plugin-system';
import { detectVariableUsage } from '../TemplateVariableProvider';
import { DashboardContext } from './DashboardProvider';

/**
 * Configuration for bulk injecting a LabelMatcher variable.
 */
export interface BulkInjectLabelMatcherConfig {
  /** Name of the variable to inject (without $ prefix) */
  variableName: string;
  /** Label name to use in the query filter */
  labelName: string;
  /** Panel keys to inject the variable into */
  panelKeys: string[];
  /** The type of injection to perform */
  injectionType: 'LabelMatcher';
}

/**
 * Configuration for bulk injecting a RangeVector variable.
 */
export interface BulkInjectRangeVectorConfig {
  /** Name of the variable to inject (without $ prefix) */
  variableName: string;
  /** Panel keys to inject the variable into */
  panelKeys: string[];
  /** The type of injection to perform */
  injectionType: 'RangeVector';
}

/**
 * Discriminated union for bulk variable injection configuration.
 */
export type BulkInjectVariableConfig = BulkInjectLabelMatcherConfig | BulkInjectRangeVectorConfig;

export interface BulkInjectResult {
  success: Array<{
    panelKey: string;
    panelName: string;
    queriesModified: number;
  }>;
  skipped: Array<{
    panelKey: string;
    panelName: string;
    reason: string;
  }>;
  failed: Array<{
    panelKey: string;
    panelName: string;
    error: string;
  }>;
}

/**
 * Bulk inject a variable into multiple panel queries.
 * Updates panel queries to include a label matcher/filter for the specified variable.
 */
export function useBulkInjectVariable() {
  const ctx = useContext(DashboardContext);
  if (!ctx) {
    throw new Error('useBulkInjectVariable must be used within DashboardProvider');
  }
  const { store } = ctx;
  const { getPlugin } = usePluginRegistry();

  const injectVariable = useCallback(
    async (config: BulkInjectVariableConfig): Promise<BulkInjectResult> => {
      const { variableName, panelKeys, injectionType } = config;
      const result: BulkInjectResult = {
        success: [],
        skipped: [],
        failed: [],
      };

      // Build the injection options based on the discriminated union type
      const injectionOptions: InjectVariableOptions =
        injectionType === 'RangeVector'
          ? { name: variableName, injectionType: 'RangeVector' }
          : {
              name: variableName,
              injectionType: 'LabelMatcher',
              labelName: config.labelName,
              operator: DEFAULT_INJECT_OPERATOR,
            };

      const panels = store.getState().panels;

      // Collect all panel updates before applying them to avoid partial state issues
      const panelUpdates: Record<string, PanelDefinition> = {};

      for (const panelKey of panelKeys) {
        const panel = panels[panelKey];
        if (!panel) {
          result.failed.push({
            panelKey,
            panelName: 'Unknown',
            error: 'Panel not found',
          });
          continue;
        }

        const panelName = panel.spec.display?.name || 'Unnamed Panel';
        const queries = panel.spec.queries;

        // Skip panels with no queries
        if (!queries || queries.length === 0) {
          result.skipped.push({
            panelKey,
            panelName,
            reason: 'No queries in panel',
          });
          continue;
        }

        let queriesModified = 0;
        let allQueriesSkipped = true;
        const modifiedQueries: QueryDefinition[] = [...queries];
        const queryErrors: string[] = [];

        // Process each query in the panel, collect all modifications
        for (let i = 0; i < queries.length; i++) {
          const queryDef = queries[i];
          if (!queryDef) continue;

          const currentQuery = queryDef.spec.plugin.spec?.query;

          // Skip if query doesn't have a query string
          if (typeof currentQuery !== 'string') {
            continue;
          }

          // Check if variable is already in this query
          if (detectVariableUsage(currentQuery, variableName)) {
            continue; // Skip this query, but keep processing others
          }

          allQueriesSkipped = false;

          try {
            // Load the plugin for this query and check if it supports injectVariable
            const pluginKind = queryDef.spec.plugin.kind;
            const pluginImpl = await getPlugin('DataQuery', pluginKind);
            const plugin = initializeDataQueryPlugin(pluginImpl);
            const injectVariableFn = getInjectVariableFunction(plugin);
            if (!injectVariableFn) {
              // Plugin does not support variable injection, skip
              continue;
            }

            // Call injectVariable plugin method
            const { spec: modifiedSpec } = await injectVariableFn.call(
              plugin,
              queryDef.spec.plugin.spec,
              injectionOptions
            );

            // Check if the spec actually changed
            if (JSON.stringify(modifiedSpec) !== JSON.stringify(queryDef.spec.plugin.spec)) {
              // Store the modified query for later batch update
              modifiedQueries[i] = produce(queryDef, (draft) => {
                draft.spec.plugin.spec = modifiedSpec;
              });
              queriesModified++;
            }
          } catch (error) {
            // Track individual query errors but continue processing others
            const queryIdentifier = queryDef.spec.alias || queryDef.spec.id || `Query ${i + 1}`;
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            queryErrors.push(`Failed to inject $${variableName} into ${queryIdentifier}: ${errorMessage}`);
          }
        }

        // Modifications successful, update query definitions and record results to show in notification banner
        if (queriesModified > 0) {
          panelUpdates[panelKey] = produce(panel, (draft) => {
            draft.spec.queries = modifiedQueries;
          });
          result.success.push({
            panelKey,
            panelName,
            queriesModified,
          });
        }

        // If there were errors, report even if some queries succeeded
        if (queryErrors.length > 0) {
          const errorSummary =
            queryErrors.length === 1
              ? queryErrors[0]!
              : `${queryErrors.length} queries failed: ${queryErrors.join(' | ')}`;
          result.failed.push({
            panelKey,
            panelName,
            error: errorSummary,
          });
        } else if (allQueriesSkipped && queriesModified === 0) {
          result.skipped.push({
            panelKey,
            panelName,
            reason: 'Variable already present in all queries',
          });
        } else if (!allQueriesSkipped && queriesModified === 0) {
          // Injection was attempted but produced no modifications
          result.skipped.push({
            panelKey,
            panelName,
            reason: 'Variable could not be injected into queries',
          });
        }
      }

      // Apply all panel updates at once using the store action for proper history/undo support
      if (Object.keys(panelUpdates).length > 0) {
        const queriesModified = result.success.reduce((acc, s) => acc + s.queriesModified, 0);
        store.getState().applyBulkVariableInjection(panelUpdates, {
          variableName,
          panelCount: result.success.length,
          queriesModified,
        });
      }

      return result;
    },
    [store, getPlugin]
  );

  return { injectVariable };
}
